//eslint-disable-next-line @next/next/no-server-import-in-page
import { NextResponse } from 'next/server'
//eslint-disable-next-line @next/next/no-server-import-in-page
import type { NextRequest } from 'next/server'

import { CookieNames } from 'server/cookies'

import { BILLING_INVOICES_ROUTE, BILLING_PAYMENT_DETAILS_ROUTE, BILLING_PLAN_ROUTE } from 'lib/constants'
import { isLocalUrl, isPermittedUrl } from 'lib/routeUtils'

// if we're testing, always enable
const isStaging =
  process.env.ENVIRONMENT === 'test'
    ? true
    : process.env.DEVELOPER_DASHBOARD_DOMAIN === 'metamask-developer-dashboard-web-staging.vercel.app'
const authPassword = process.env.ENVIRONMENT === 'test' ? 'test' : process.env.AUTH_PASSWORD
export const unprotectedRoutes = [
  '/login',
  '/register',
  '/deleted',
  '/forgotpassword',
  '/forgotverify',
  '/verify/',
  '/logout'
]

export const isMfaVerified = (mfaCookie: string) => mfaCookie === '' || +mfaCookie > 0
export const redirectPermitted = (redirect?: string | null): redirect is string =>
  typeof redirect === 'string' && (isLocalUrl(redirect) || isPermittedUrl(redirect))
export const isRouteProtected = (pathname: string) => !unprotectedRoutes.some(path => pathname.startsWith(path))
export const makeQueryString = (searchParams: URLSearchParams): string =>
  searchParams.size > 0 ? '?' + searchParams.toString() : ''

const handleHasSession = (request: NextRequest): NextResponse | undefined => {
  const url = request.nextUrl
  const verified = request.cookies.get(CookieNames.VERIFIED)?.value === 'true'
  const mfa = request.cookies.get(CookieNames.MFA)?.value
  const redirect = url.searchParams.get('redirect')
  // To be mfa verified, the cookie value must be empty, or a numerical ts when it was verified by us
  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  const mfaVerified = isMfaVerified(mfa)
  // we should be redirected somewhere if authenticated
  if (redirectPermitted(redirect)) {
    return NextResponse.redirect(new URL(redirect, request.url))
  }

  // we're on an authenticated route
  if (isRouteProtected(url.pathname) || url.searchParams.get('mm_auth')) {
    if (!mfaVerified) return NextResponse.redirect(new URL(`/login${makeQueryString(url.searchParams)}`, request.url))
    if (!verified && url.pathname !== '/resendverify') {
      return NextResponse.redirect(new URL(`/resendverify${makeQueryString(url.searchParams)}`, request.url))
    }
    return
  }

  // if we're on registration, redirect to resendverify
  if (['/register', '/login'].some(p => url.pathname.startsWith(p))) {
    if (verified && mfaVerified) return NextResponse.redirect(new URL('/', request.url))
    if (!verified)
      return NextResponse.redirect(new URL(`/resendverify${makeQueryString(url.searchParams)}`, request.url))
  }
}

const handleNoSession = (request: NextRequest): NextResponse | undefined => {
  // and we're on an authenticated route
  if (isRouteProtected(request.nextUrl.pathname)) {
    return NextResponse.redirect(
      new URL(
        `/login${
          request.nextUrl.pathname !== '/'
            ? '?' + new URLSearchParams({ redirect: request.nextUrl.pathname }).toString()
            : ''
        }`,
        request.url
      )
    )
  }
}

const requireAuthError = NextResponse.json(
  { error: 'Auth required' },
  {
    headers: {
      'WWW-Authenticate': 'Basic realm="Secure Area"'
    },
    status: 401
  }
)

const handleStagingSecurity = (request: NextRequest): NextResponse | undefined => {
  // allow qa bypasses
  if (request.nextUrl.searchParams.get('bypass') === '84450394') return
  // otherwise prompt browser with auth form
  const basicAuth = request.headers.get('authorization')
  if (!basicAuth) return requireAuthError

  const authValue = basicAuth.split(' ')[1]
  const [user, pwd] = atob(authValue).split(':')

  if (user !== 'admin' || pwd !== authPassword) return requireAuthError
}

const OLD_PLAN_ROUTE = '/settings/payment'
const OLD_INVOICES_ROUTE = '/settings/billing'
const OLD_PAYMENT_DETAILS_ROUTE = '/settings/payment-details'

// This function can be marked `async` if using `await` inside
export function middleware(request: NextRequest) {
  // These redirects are here because of route changes under settings. This way links in older invoice emails will still work
  if (request.nextUrl.pathname.includes(OLD_PAYMENT_DETAILS_ROUTE)) {
    const newUrl = `${request.url.split(OLD_PAYMENT_DETAILS_ROUTE)[0]}${BILLING_PAYMENT_DETAILS_ROUTE}`
    return NextResponse.redirect(newUrl)
  }

  if (
    request.nextUrl.pathname.includes(OLD_PLAN_ROUTE) &&
    !request.nextUrl.pathname.includes(OLD_PAYMENT_DETAILS_ROUTE)
  ) {
    const newUrl = `${request.url.split(OLD_PLAN_ROUTE)[0]}${BILLING_PLAN_ROUTE}`
    return NextResponse.redirect(newUrl)
  }

  if (request.nextUrl.pathname.includes(OLD_INVOICES_ROUTE)) {
    const newUrl = `${request.url.split(OLD_INVOICES_ROUTE)[0]}${BILLING_INVOICES_ROUTE}`
    return NextResponse.redirect(newUrl)
  }

  if (isStaging) {
    const interception = handleStagingSecurity(request)
    if (interception) return interception
  }

  const hasSession = !!request.cookies.get(CookieNames.SESSION)

  // we potentially have a valid session
  if (hasSession) {
    return handleHasSession(request)
  }

  // if we do not have any valid session
  if (!hasSession) {
    return handleNoSession(request)
  }
}

export const config = {
  matcher: [
    '/((?!api|images|js|svg|_next|_axiom|favicon|fonts|manifest|crossdomain|MP_verify_hlHD16QtOjZNcBPT|mockServiceWorker).*)'
  ]
}
